import { Injectable, inject, computed, effect } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  RoleEnv,
  ROLES_BY_ENV,
  FRM_NONPROD_ADMIN,  FRM_NONPROD_ANALYST,  FRM_NONPROD_READONLY,
  FRM_PROD_ADMIN,     FRM_PROD_ANALYST,     FRM_PROD_READONLY,
  FRM_LENDER,         FRM_LENDER_ADMIN,     FRM_CUSTODIAN
} from '../constants/iam-roles.constants';

/**
 * EnvRoleService — the single authoritative source for environment-scoped
 * role validation.
 *
 * WHY this service exists
 * ───────────────────────
 * /userdetails returns ALL IAG groups a user belongs to across ALL environments
 * (e.g. a user can have both SG-IC1-NONPROD-ANALYST-ROLE and SG-IC1-PROD-ADMIN-ROLE).
 * Without isolation:
 *   • A PROD-ADMIN user accessing a DEVL deployment would get full admin UI in DEVL.
 *   • A NONPROD-ANALYST user hitting PROD APIs via a stale session would succeed.
 *
 * This service solves that by:
 *   1. Detecting which environment the app is deployed in (from environment.env).
 *   2. Computing `effectiveRoles` — the subset of the user's roles that belong
 *      to the deployed environment tier.
 *   3. Exposing `hasRole()` / `isAdmin` / `isAnalyst` / `isReadonly` signals that
 *      ONLY consider effective roles.
 *
 * ALL components and guards must use this service instead of calling
 * AuthService.hasRole() directly (except for the very first auth check).
 *
 * Design principles
 * ─────────────────
 * • Config-driven: role → env mapping lives in ROLES_BY_ENV (iam-roles.constants.ts).
 * • No scattered environment checks — all logic is here.
 * • No hardcoded strings — all role names come from iam-roles.constants.ts.
 * • No cross-environment state: effectiveRoles is a pure computed signal.
 */
@Injectable({ providedIn: 'root' })
export class EnvRoleService {
  private readonly auth = inject(AuthService);
  private lastDebugSignature = '';

  // ── Deployment environment ─────────────────────────────────────────────────

  /**
   * The tier of the running deployment, derived from environment.env.
   *   'PROD'                         → PROD
   *   'LOCAL' | 'DEVL' | anything else → NONPROD
   *
   * This value is a constant for the lifetime of the app — it never changes.
   */
  readonly deployedEnv: RoleEnv = environment.env === 'PROD' ? 'PROD' : 'NONPROD';

  // ── Pre-computed role sets for O(1) lookup ─────────────────────────────────

  private readonly validRoleSet = new Set<string>(
    ROLES_BY_ENV[this.deployedEnv].map(r => r.toUpperCase())
  );

  // ── Reactive signals ───────────────────────────────────────────────────────

  /**
   * The user's roles from /userdetails filtered to only those valid for the
   * deployed environment tier.
   *
   * Example: user has [NONPROD-ANALYST, PROD-ADMIN] in DEVL deployment →
   *   effectiveRoles = ['SG-IC1-NONPROD-ANALYST-ROLE']
   *   (PROD-ADMIN is silently excluded — it has no meaning here)
   */
  readonly effectiveRoles = computed(() =>
    this.auth.userRoles().filter(r => this.validRoleSet.has(r.trim().toUpperCase()))
  );

  /**
   * True when the user has at least one role valid for the deployed environment.
   * Use in route guards to block cross-environment access.
   */
  readonly hasEnvRoles = computed(() => this.effectiveRoles().length > 0);

  /** True when the user is an ADMIN in the deployed environment */
  readonly isAdmin = computed(() =>
    this.hasEffectiveRole(this.deployedEnv === 'NONPROD' ? FRM_NONPROD_ADMIN : FRM_PROD_ADMIN)
  );

  /** True when the user is an ANALYST in the deployed environment */
  readonly isAnalyst = computed(() =>
    this.hasEffectiveRole(this.deployedEnv === 'NONPROD' ? FRM_NONPROD_ANALYST : FRM_PROD_ANALYST)
  );

  /** True when the user is READONLY in the deployed environment */
  readonly isReadonly = computed(() =>
    this.hasEffectiveRole(this.deployedEnv === 'NONPROD' ? FRM_NONPROD_READONLY : FRM_PROD_READONLY)
  );

  /** True when the user is an External Lender (write access to FRM Data; Preparer for Lender Attestation) */
  readonly isLender = computed(() => this.hasEffectiveRole(FRM_LENDER));

  /** True when the user is an External Lender Admin (approves Lender Attestation submissions) */
  readonly isLenderAdmin = computed(() => this.hasEffectiveRole(FRM_LENDER_ADMIN));

  /** True when the user is an External Custodian (read-only access; US Bank) */
  readonly isCustodian = computed(() => this.hasEffectiveRole(FRM_CUSTODIAN));

  constructor() {
    effect(() => {
      if (!this.isLocalDebugHost()) {
        return;
      }

      // Debug-only diagnostics; never let a role-resolution failure break change detection.
      try {
        const localStorageRoles = this.readLocalStorageValue('frm.dev.roles');
        const debugState = {
          deployedEnv: this.deployedEnv,
          hostname: window.location.hostname,
          localStorageRoles,
          rawRoles: this.auth.userRoles(),
          effectiveRoles: this.effectiveRoles(),
          isAdmin: this.isAdmin(),
          isAnalyst: this.isAnalyst(),
          isReadonly: this.isReadonly(),
          isLender: this.isLender(),
          isLenderAdmin: this.isLenderAdmin(),
          isCustodian: this.isCustodian()
        };

        const signature = JSON.stringify(debugState);
        if (signature === this.lastDebugSignature) {
          return;
        }

        this.lastDebugSignature = signature;
        (window as Window & { __frmRoleDebug?: unknown }).__frmRoleDebug = debugState;
        console.log('[EnvRoleService] Active role state', debugState);
      } catch {
        // Role source unavailable (e.g. test harness with a partial auth mock) -- skip diagnostics.
      }
    });
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  /**
   * Environment-scoped role check.
   *
   * Like AuthService.hasRole() but ONLY considers roles valid for the deployed
   * environment. Cross-environment roles are transparently excluded.
   *
   * @param roles  One or more exact IAG role strings (from iam-roles.constants.ts)
   * @returns true if the user has at least one of the supplied roles AND that
   *          role is valid for the current deployment environment.
   */
  hasRole(...roles: string[]): boolean {
    const effective = new Set(this.effectiveRoles().map(r => r.trim().toUpperCase()));
    return roles.some(r => effective.has(r.trim().toUpperCase()));
  }

  /**
   * Checks whether the current user holds ANY role for a given environment tier.
   * Useful for building "you cannot access PROD from a NONPROD session" messages.
   *
   * @param env  The target RoleEnv to test
   */
  canAccessEnv(env: RoleEnv): boolean {
    const targetSet = new Set<string>(ROLES_BY_ENV[env].map(r => r.toUpperCase()));
    return this.auth.userRoles().some(r => targetSet.has(r.trim().toUpperCase()));
  }

  /**
   * Returns a human-readable label for the deployed environment.
   * Used in template banners and access-denied messages.
   */
  envLabel(): string {
    return this.deployedEnv === 'PROD' ? 'Production (PROD)' : 'Non-Production (NONPROD)';
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private hasEffectiveRole(role: string): boolean {
    return this.effectiveRoles().some(r => r.trim().toUpperCase() === role.toUpperCase());
  }

  private isLocalDebugHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  }

  private readLocalStorageValue(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}

